import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollAnimate } from '../components/ScrollAnimate';
import { SEO } from '../components/SEO';
import { CheckIcon } from '../components/icons/SiteIcons';

const tech = [
  'Wazuh',
  'Suricata',
  'TheHive',
  'Velociraptor',
  'Proxmox',
  'WireGuard',
  'Nginx',
  'Cloudflare',
  'Docker',
  'Elastic Stack',
  'Ansible',
  'Terraform',
];

function TierCard({
  label,
  title,
  price,
  desc,
  features,
  buttonText,
}: {
  label: string;
  title: string;
  price: string;
  desc: string;
  features: string[];
  buttonText: string;
}) {
  return (
    <article className="card-site flex h-full flex-col">
      <p className="text-label-site text-[#0064E0]">{label}</p>
      <h3 className="text-card-title mt-2">{title}</h3>
      <p className="mt-3 text-[28px] font-bold text-[#0064E0]">{price}</p>
      <hr className="my-5 border-[#E4E6EB]" />
      <p className="text-body-site">{desc}</p>
      <ul className="mt-5 flex-1 space-y-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[17px] text-[#1C1E21]">
            <CheckIcon className="mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      <Link
        to="/contact"
        className="mt-auto block w-full rounded-lg bg-[#0064E0] py-3.5 text-center text-base font-semibold text-white transition-colors hover:bg-[#0056C8]"
      >
        {buttonText}
      </Link>
    </article>
  );
}

export function ServicesPage() {
  return (
    <>
      <SEO
        title="Managed Security Services — logNsec"
        description="Remote-first managed detection and response. Your security team, delivered globally."
        canonical="/services"
      />
      <section className="bg-[#F5F5F5] px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-4xl">
          <ScrollAnimate>
            <p className="text-label-site text-[#65676B]">Managed Services</p>
            <h1 className="text-section mt-3">
              Your Security Team.
              <br />
              Rendered Remotely.
            </h1>
            <p className="mt-6 text-body-site">
              Enterprise-grade managed security services delivered entirely remotely using
              secure infrastructure and AI-powered tooling.
            </p>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-white px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate>
            <h2 className="text-section text-center md:text-left">How It Works</h2>
          </ScrollAnimate>
          <ScrollAnimate className="mt-12">
            <div className="grid grid-cols-1 items-stretch gap-8 md:grid-cols-3">
              {[
                {
                  n: '01',
                  t: 'We Assess',
                  b: 'We audit your infrastructure, identify gaps, and design a security architecture tailored to your business needs.',
                },
                {
                  n: '02',
                  t: 'We Deploy',
                  b: 'We deploy Wazuh agents, network sensors, and compliance tooling to your environment via secure remote access.',
                },
                {
                  n: '03',
                  t: 'We Protect',
                  b: '24/7 monitoring, real-time alerts, monthly reports, and incident response — all delivered remotely by our team.',
                },
              ].map((s) => (
                <div key={s.n} className="card-site flex h-full flex-col">
                  <span className="text-[48px] font-semibold leading-none text-[#0064E0]">
                    {s.n}
                  </span>
                  <h3 className="text-card-title mt-4">{s.t}</h3>
                  <p className="mt-4 text-body-site">{s.b}</p>
                </div>
              ))}
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#F5F5F5] px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate>
            <h2 className="text-section">Service Tiers</h2>
          </ScrollAnimate>
          <ScrollAnimate className="mt-12">
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2">
              <TierCard
                label="Tier 1"
                title="Managed Detection & Response"
                price="$500 – $1,500/month"
                desc="24/7 monitoring with Wazuh SIEM, real-time alert triage, and incident response for businesses with on-premise servers."
                features={[
                  'Wazuh agent deployment (10 endpoints)',
                  '24/7 alert monitoring',
                  'WhatsApp + email alerts',
                  'Monthly security report',
                  '4hrs incident response/month',
                  'ShieldSME dashboard included',
                ]}
                buttonText="Get Started"
              />
              <TierCard
                label="Tier 2"
                title="Full Managed Security"
                price="$2,000 – $5,000/month"
                desc="Complete security coverage including network monitoring, forensic investigations, and compliance documentation."
                features={[
                  'Everything in Tier 1',
                  'Network intrusion detection',
                  'Forensic investigation capability',
                  'Compliance documentation portal',
                  'Quarterly security assessment',
                  'Up to 50 endpoints',
                ]}
                buttonText="Get Started"
              />
              <TierCard
                label="Tier 3"
                title="Enterprise Security"
                price="$5,000 – $15,000/month"
                desc="Custom security architecture, dedicated analyst, SLA guarantees, and quarterly security assessments."
                features={[
                  'Everything in Tier 2',
                  'Dedicated security analyst',
                  '2-hour SLA guarantee',
                  'Hybrid cloud/on-prem management',
                  'Unlimited endpoints',
                  'Monthly executive briefing',
                ]}
                buttonText="Contact Sales"
              />
              <TierCard
                label="Custom"
                title="Custom Engagements"
                price="Contact for pricing"
                desc="Bespoke solutions for unique infrastructure requirements, compliance needs, and specialized security challenges."
                features={[
                  'Bespoke security architecture',
                  'Custom AI security tooling',
                  'Regulatory compliance consulting',
                  'On-site visits available',
                  'White-label options',
                  'Dedicated engagement manager',
                ]}
                buttonText="Schedule a Call"
              />
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-white px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl text-center">
          <ScrollAnimate>
            <h2 className="text-section">
              Powered by Enterprise-Grade
              <br />
              Open Source
            </h2>
          </ScrollAnimate>
          <ScrollAnimate className="mt-10">
            <div className="flex flex-wrap justify-center gap-3">
              {tech.map((t) => (
                <span
                  key={t}
                  className="rounded-lg border border-[#E4E6EB] bg-[#F0F2F5] px-4 py-2 text-sm font-medium text-[#1C1E21]"
                >
                  {t}
                </span>
              ))}
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#0064E0] px-5 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollAnimate>
            <h2 className="text-[32px] font-bold text-white md:text-[40px]">
              Let&apos;s discuss your security needs
            </h2>
            <Link
              to="/contact"
              className="mt-8 inline-flex rounded-lg bg-white px-8 py-3.5 text-base font-semibold text-[#0064E0] transition-colors hover:bg-[#F0F2F5]"
            >
              Schedule a Consultation
            </Link>
          </ScrollAnimate>
        </div>
      </section>
    </>
  );
}
